import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useNavigate } from 'react-router-dom';

const LeadsContext = createContext();

export const useLeads = () => {
    const context = useContext(LeadsContext);
    if (!context) {
        throw new Error('useLeads must be used within a LeadsProvider');
    }
    return context;
};

export const LeadsProvider = ({ children }) => {
    const { user } = useAuth();
    const { toast } = useToast();
    // Defensive check for useNavigate
    let navigate;
    try {
        navigate = useNavigate();
    } catch (e) {
        console.warn("LeadsProvider: useNavigate called outside of Router context. Navigation features will be disabled.", e);
    }
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        byStatus: { prospecto: 0, calificado: 0, contactado: 0, negociacion: 0, cerrado: 0, perdido: 0 },
        totalValue: 0
    });

    const API_URL = '/api.php?action=leads';

    const showPlanToast = (message) => {
        toast({
            title: "Acceso restringido",
            description: message,
            variant: "destructive",
            action: (
                <ToastAction altText="Ver planes" onClick={() => navigate('/subscription')}>
                    Ver planes
                </ToastAction>
            )
        });
    };

    const safeJsonParse = (value, fallback) => {
        try {
            return JSON.parse(value);
        } catch (err) {
            return fallback;
        }
    };

    const safeGetLocalStorage = (key, fallback) => {
        try {
            const raw = localStorage.getItem(key);
            if (raw === null || raw === undefined) return fallback;
            return safeJsonParse(raw, fallback);
        } catch (err) {
            return fallback;
        }
    };

    const normalizeLeadsResponse = (data) => {
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.leads)) return data.leads;
        if (data && Array.isArray(data.data)) return data.data;
        return [];
    };

    const fetchLeads = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            if (import.meta.env.VITE_SIMULATE_BAD_API === '1') {
                setLeads([]);
                setError("No se pudieron cargar los leads. Intenta de nuevo.");
                setLoading(false);
                return;
            }
            // PRO Check: Could be done here or handled by backend error
            const response = await fetch(API_URL + (user.id ? `&user_id=${user.id}` : ''));
            if (!response.ok) {
                console.warn('Leads response not ok:', response.status);
                setLeads([]);
                setError("No se pudieron cargar los leads. Intenta de nuevo.");
                setLoading(false);
                return;
            }

            let data = null;
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                data = await response.json();
            } else {
                console.warn('Leads response is not JSON:', contentType);
                setLeads([]);
                setError("No se pudieron cargar los leads. Intenta de nuevo.");
                setLoading(false);
                return;
            }

            if (data && data.error) {
                if (data.error === 'PLAN_REQUIRED' || data.error === 'LIMIT_REACHED') {
                    showPlanToast(data.message || "Tu plan actual no permite acceder al CRM de leads.");
                    setLeads([]);
                    setLoading(false);
                    return;
                }
                console.warn('Leads error payload:', data.error);
                setLeads([]);
                setError("No se pudieron cargar los leads. Intenta de nuevo.");
                setLoading(false);
                return;
            }

            const normalized = normalizeLeadsResponse(data);
            if (!Array.isArray(normalized)) {
                console.warn('Leads normalized to non-array', normalized);
            }
            const safeLeads = Array.isArray(normalized) ? normalized : [];
            const mappedLeads = safeLeads.map(l => ({
                ...l,
                budget: parseFloat(l.budget) || 0,
                property: l.property_interest || ''
            }));
            setLeads(mappedLeads);
            calculateStats(mappedLeads);
        } catch (error) {
            console.error('Error fetching leads:', error);
            setLeads([]);
            setError("No se pudieron cargar los leads. Intenta de nuevo.");
            toast({ title: "Error", description: "No se pudieron cargar los leads. Intenta de nuevo.", variant: "destructive" });
        } finally {
            setLoading(false);
        }
    }, [user]);

    const calculateStats = (data) => {
        if (!Array.isArray(data)) return;
        const newStats = {
            total: data.length,
            byStatus: {},
            totalValue: data.reduce((sum, lead) => sum + (parseFloat(lead.budget) || 0), 0)
        };

        data.forEach(lead => {
            const status = lead.status || 'prospecto';
            newStats.byStatus[status] = (newStats.byStatus[status] || 0) + 1;
        });

        setStats(newStats);
    };

    const createLead = async (leadData) => {
        try {
            const payload = { ...leadData, user_id: user.id };
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();

            if (result.success) {
                toast({ title: "Lead creado exitosamente" });
                fetchLeads(); // Refresh
                return true;
            }
            if (response.status === 402 || response.status === 403 || result.error === 'PLAN_REQUIRED' || result.error === 'LIMIT_REACHED') {
                showPlanToast(result.message || "Tu plan actual no permite crear más leads.");
                return false;
            }
            setError("No se pudieron guardar los cambios. Intenta de nuevo.");
            toast({ title: "Error", description: "No se pudieron guardar los cambios. Intenta de nuevo.", variant: "destructive" });
            return false;
        } catch (error) {
            toast({ title: "Error", description: error.message, variant: "destructive" });
            return false;
        }
    };

    const updateLead = async (id, updates) => {
        // Optimistic Update
        const originalLeads = [...leads];
        setLeads(leads.map(l => l.id === id ? { ...l, ...updates } : l));

        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, ...updates })
            });
            const result = await response.json();
            if (!result.success) {
                setError("No se pudo actualizar el lead.");
                toast({ title: "Error", description: "No se pudo actualizar el lead.", variant: "destructive" });
                return;
            }

            // Refetch to be sure or just stick with optimistic
            // fetchLeads(); 
        } catch (error) {
            // Revert
            setLeads(originalLeads);
            toast({ title: "Error", description: "No se pudo actualizar el lead", variant: "destructive" });
        }
    };

    const deleteLead = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este lead?')) return;

        try {
            const response = await fetch(`${API_URL}&id=${id}`, { method: 'DELETE' });
            const result = await response.json();

            if (result.success) {
                setLeads(leads.filter(l => l.id !== id));
                toast({ title: "Lead eliminado" });
            } else {
                setError("No se pudo eliminar el lead.");
                toast({ title: "Error", description: "No se pudo eliminar el lead.", variant: "destructive" });
            }
        } catch (error) {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        }
    };

    useEffect(() => {
        if (user) fetchLeads();
    }, [user, fetchLeads]);

    return (
        <LeadsContext.Provider value={{
            leads,
            loading,
            error,
            stats,
            fetchLeads,
            createLead,
            updateLead,
            deleteLead
        }}>
            {children}
        </LeadsContext.Provider>
    );
};
